import * as React from 'react';
import classNames from 'classnames';
import { debounce } from 'lodash-es';
import {
  BaseAutocomplete,
  BaseAutocompleteProps,
} from '../autocomplete/BaseAutocomplete/BaseAutocomplete';
import * as Classes from '../common/classes';
import { Icon } from '../icon/Icon';
import { mcSearch } from '@mc/react-ui-icons';

export interface CancellableApiPromise<T> extends Promise<T> {
  cancel?: (reason?: string) => CancellableApiPromise<T>;
}

type Props<API_TYPE, VAL_TYPE> = Omit<
  BaseAutocompleteProps<API_TYPE, VAL_TYPE> & {
    onSearchItems: (text: string) => CancellableApiPromise<API_TYPE[]>;
    // onClearItems?: () => void;
    minSearchString?: number;
    debounceDelay?: number;
    // initialItems?: API_TYPE[];
  },
  | 'tags'
  | 'items'
  | 'onChange'
  | 'onSearchByText'
  | 'touched'
  | 'error'
  | 'inline'
  | 'getDropDownIcon'
  | 'actions'
  | 'showMenuOnFocus'
  | 'alwaysExpanded'
  | 'tree'
  | 'noItemsFound'
  | 'hasDropdownIcon'
  | 'allowCreateItems'
  | 'initialItems'
  | 'onClearItems'
  | 'value'
  | 'useIdAsValue'
  | 'onTextChange'
  | 'getNewItem'
  | 'dropDownIconClickOpenMenu'
> & {
  /**
   * Pokud při není vybrána položka z fulltext nabídky a uživatel stiskne enter, zavolá se tato metopda s vyplněným textem hledání.
   */
  onSearchByText: (val: string) => void;
  /**
   * Voláno při výběru položky z fulltext nabídky (nikdy není voláno s null).
   * @param val
   */
  onSelect: (val: VAL_TYPE) => void;
};

/**
 * Wrapper pro vytvoření cancellable api promise z předaného promise - pro možnost použití v api a jeho cancel.
 * @param promise promise
 * @param onCancel cancel callback
 */
export function cancellableApi<T>(
  promise: Promise<T>,
  onCancel?: () => void
): CancellableApiPromise<T> {
  let cancel: ((reason: string) => CancellableApiPromise<T>) | null = null;
  let cancellable: CancellableApiPromise<T>;
  cancellable = new Promise((resolve, reject) => {
    cancel = () => {
      try {
        if (onCancel) {
          onCancel();
        }
      } catch (e) {
        reject(e);
      }
      return cancellable;
    };
    promise.then(resolve, reject);
  });
  if (cancel) {
    cancellable.cancel = cancel;
  }
  return cancellable;
}

/**
 * Fulltext komponenta pro hledání buď textu z nabídky (hledání na serveru) nebo dle zadaného obnecného textu (typicky se přesměruje na nějakou stránku s výsledky hledání daného textu).
 */
export function NavbarFullText<API_TYPE, VAL_TYPE = API_TYPE>({
  minSearchString = 3,
  placeholder = 'Vyhledat...',
  debounceDelay = 500,
  onSearchItems,
  className,
  onSearchByText,
  onSelect,
  ...other
}: Props<API_TYPE, VAL_TYPE>) {
  const [items, setItems] = React.useState<API_TYPE[]>([]);
  const [searchText, setText] = React.useState('');
  const [searching, setSearching] = React.useState<boolean>(false);

  const currentApiSearch =
    React.useRef<CancellableApiPromise<API_TYPE[]>>(null);
  const callSearch = React.useRef<boolean>(false);
  const currentSearchText = React.useRef<String>('');

  const search = React.useCallback(
    debounce((text: any) => {
      if (callSearch.current) {
        setSearching(true);

        if (currentApiSearch.current && currentApiSearch.current.cancel) {
          currentApiSearch.current.cancel();
        }

        let call = onSearchItems(text);
        // @ts-ignore
        currentApiSearch.current = call;

        call
          .then((items) => {
            if (
              currentApiSearch.current === call &&
              currentSearchText.current == text
            ) {
              // @ts-ignore
              currentApiSearch.current = null;
              setItems(items);
            }
          })
          .finally(() => {
            setSearching(false);
          });
      }
    }, debounceDelay),
    [debounceDelay, onSearchItems]
  );

  const useMinSearchString = minSearchString || 1;

  let noItemsFoundText: string;
  if (searching) {
    noItemsFoundText = 'Probíhá vyhledávání...';
  } else {
    if (searchText && searchText.length >= useMinSearchString) {
      noItemsFoundText = 'Pro zadaný řetězec nebyly nalezeny žádné záznamy';
    } else {
      if (useMinSearchString == 1) {
        noItemsFoundText = 'Pro vyhledání zadejte text (alespoň jeden znak)';
      } else if (useMinSearchString <= 3) {
        noItemsFoundText = `Pro vyhledání zadejte text (alespoň ${useMinSearchString} znaky)`;
      } else {
        noItemsFoundText = `Pro vyhledání zadejte text (alespoň ${useMinSearchString} znaků)`;
      }
    }
  }

  // @ts-ignore
  // @ts-ignore
  return (
    <div className={Classes.NAVBAR_FULLTEXT}>
      <BaseAutocomplete<API_TYPE, VAL_TYPE>
        useAutocompleteInternalFilter={false}
        className={classNames(
          className,
          Classes.BASE_AUTOCOMPLETE,
          Classes.FULLTEXT,
          {
            ['searching']: searching,
          }
        )}
        noItemsFound={noItemsFoundText}
        shouldItemRender={(_, __) => true}
        onCloseMenu={() => {
          // @ts-ignore
          if (other.value === null || other.value === undefined) {
            setText('');
            setItems([]);
          }
        }}
        showMenuOnFocus={false}
        // dropDownIconClickOpenMenu={false}
        hasDropdownIcon={false}
        inputProps={{
          // @ts-ignore
          leftElement: (
            <div className={Classes.FULLTEXT_LEFT_ICON}>
              <Icon fixedWidth icon={mcSearch} />
            </div>
          ),
        }}
        // getDropDownIcon={() => faSearch}
        placeholder={placeholder}
        tags
        onSearchByText={onSearchByText}
        onChange={(val) => {
          val && onSelect(val);
        }}
        {...other}
        items={searching ? [] : items}
        onTextChange={(text) => {
          setText(text);
          currentSearchText.current = text;
          if (useMinSearchString <= text.length) {
            setSearching(true);

            setItems([]);
            // onClearItems && onClearItems();

            callSearch.current = true;
            search(text);
          } else {
            if (currentApiSearch.current && currentApiSearch.current.cancel) {
              currentApiSearch.current.cancel();
            }
            // @ts-ignore
            currentApiSearch.current = null;
            callSearch.current = false;
            if (searching) {
              setSearching(false);
            }

            if (items && items.length > 0) {
              setItems([]);
              // onClearItems && onClearItems();
            }
          }
        }}
      />
    </div>
  );
}
